/* Motion, through GSAP, and the one decision every animation here shares: whether to move at all.
 *
 * **GSAP rather than CSS keyframes, because the Overview's motion is choreography rather than a set of
 * independent effects.** The reference builds its dashboard in a measured order — header, then the KPI
 * row, then the action tiles, then the panels — and a timeline is the honest expression of an order:
 * each step is placed relative to the last, so tightening one changes the rest with it. Keyframes
 * would need a hand-computed `animation-delay` per element, which is a schedule nobody can read.
 *
 * GSAP 3.15 is bundled at build time and fetches nothing at runtime, so it holds hard rule 13; it has
 * no dependencies. Its licence is GSAP's "standard no-charge" licence rather than an OSI one — free for
 * commercial use, with a field-of-use restriction on building a Webflow-competing site builder that
 * this product is not. Recorded here because §11 says to check the licence rather than assume it.
 *
 * **Every hook here does nothing unless the reader can be asked, and has said yes.** `motionAllowed`
 * answers false when `matchMedia` is missing as well as when reduced motion is requested. The first
 * case is not an edge: jsdom has no `matchMedia`, so every component test renders the settled page —
 * the final number rather than a frame of a count-up — and an environment that cannot report the
 * preference gets the version that cannot hurt anybody. Moving is the thing that needs permission.
 *
 * **The final state is always what React rendered**, and every animation starts from there and is
 * reverted to it. A count-up writes the real figure first and only then rewinds it; a reveal is a
 * `from` tween, so the resting style is the stylesheet's. A page whose animation fails half-way — an
 * exception, a hidden tab, a killed tween — therefore rests on the truth rather than on a frame.
 */

import { gsap } from 'gsap';

import { embeddedRuntime } from '@/shared/embedded';
import { createContext, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

/** Whether this page may animate: a `matchMedia` to ask, and no reduced-motion preference. */
export function motionAllowed(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The three curves the Overview uses, named so a reader of a timeline sees intent, not a string.
 *
 * `enter` decelerates hard — an element arriving should settle, never bounce, which is the brief's
 * own word. `draw` is symmetric because a line being drawn has no destination to brake into. `count`
 * spends most of its time near the final value, so the figure a reader reads is the real one for as
 * long as possible. */
export const EASE = {
  enter: 'power3.out',
  draw: 'power2.inOut',
  count: 'power2.out',
} as const;

/** When each beat of a page's entrance lands, as `performance.now()` milliseconds, by name.
 *
 * **Why a component deep in the page needs to know.** The page's entrance fades a panel in; the panel's
 * *contents* — a chart line drawing, a figure counting, a list settling — are separate animations that
 * run when their data arrives. With the data already cached that is the same instant as the page, so
 * the line would draw while its panel is still transparent and the reader would arrive at a finished
 * chart. A cue says "your panel is visible from here", and a reveal that is ready earlier waits for
 * it; one whose data arrives later than its cue simply runs.
 *
 * Absolute times rather than offsets, because the reveals start in effects that run *before* the
 * page's own (React runs a child's layout effect first), so the page cannot hand them a timeline that
 * does not exist yet — but it can say, from its first render, when each beat will be.
 */
export type Cues = Readonly<Record<string, number>>;

const CueContext = createContext<Cues>({});

/** Provides a page's cues to the reveals inside it. */
export const CueProvider = CueContext.Provider;

/** Seconds from now until `cue` lands: nothing for an unnamed cue, an unknown one or one already past. */
function secondsUntil(cues: Cues, cue: string | undefined): number {
  if (cue === undefined) return 0;
  const at = cues[cue];
  if (at === undefined) return 0;
  return Math.max(0, (at - performance.now()) / 1000);
}

/** Forces an animation to its end if it has not finished `slack` seconds after it should have.
 *
 * **Why an animation needs a failsafe at all.** GSAP advances on `requestAnimationFrame`, and a
 * browser stops delivering frames to a hidden tab — so a page opened in a background tab, or a pane a
 * tool keeps hidden, holds every tween at its *first* frame: figures read 0, panels sit transparent.
 * The design's motion layer carries the same guard for the same reason. `progress(1)` renders the
 * final state synchronously, without a frame, so the page lands on the truth either way. Returns the
 * cancel for the effect's cleanup. */
function settleWithin(
  animation: gsap.core.Animation,
  seconds: number,
  slack = 0.6,
): () => void {
  const timer = window.setTimeout(
    () => {
      if (animation.progress() < 1) animation.progress(1);
    },
    (seconds + slack) * 1000,
  );
  return () => {
    window.clearTimeout(timer);
  };
}

/** A one-off entrance timeline over a region, reverted when the region unmounts.
 *
 * **`gsap.context` is what makes this safe under React.** Every tween created inside it is recorded,
 * and `revert` restores each element's style to what it was before — so StrictMode's mount, unmount,
 * mount in development replays the entrance cleanly instead of stacking a second `from` on top of the
 * first one's starting frame, which is how an element ends up stuck at opacity zero.
 *
 * `useLayoutEffect` rather than `useEffect`: the first `from` frame is applied before the browser
 * paints, so nothing is ever seen at its resting position and then yanked back to the start.
 */
export function useEntrance<T extends HTMLElement>(
  build: (scope: T, timeline: gsap.core.Timeline) => void,
): RefObject<T> {
  const scope = useRef<T>(null);
  /* Held in a ref so the effect runs once per mount whatever the caller's closure captures. An
     entrance is about arriving; re-running it on a re-render would replay the page's arrival every
     time a query refetched. */
  const builder = useRef(build);
  builder.current = build;

  useLayoutEffect(() => {
    const root = scope.current;
    if (root === null || !motionAllowed()) return undefined;
    let cancel = (): void => undefined;
    const context = gsap.context(() => {
      const timeline = gsap.timeline({ defaults: { ease: EASE.enter, duration: 0.5 } });
      builder.current(root, timeline);
      cancel = settleWithin(timeline, timeline.duration());
    }, root);
    return () => {
      cancel();
      context.revert();
    };
  }, []);

  return scope;
}

/** A reveal that runs once, the first time `ready` turns true — for content that arrives after the
 * page has: a chart whose series loaded, a list whose rows did.
 *
 * Separate from `useEntrance` because the two answer different questions. The entrance is the page
 * arriving and happens on mount; this is *data* arriving, which happens whenever the read lands —
 * immediately from cache, or seconds later on a cold one — and must happen exactly once either way.
 * `cue` names the beat of the page's entrance after which the content may start; see `Cues`.
 */
export function useReveal<T extends HTMLElement | SVGElement>(
  ready: boolean,
  build: (scope: T, timeline: gsap.core.Timeline) => void,
  cue?: string,
): RefObject<T> {
  const scope = useRef<T>(null);
  const builder = useRef(build);
  builder.current = build;
  const shown = useRef(false);
  const cues = useContext(CueContext);

  useLayoutEffect(() => {
    const root = scope.current;
    if (!ready || root === null || shown.current) return undefined;
    shown.current = true;
    if (!motionAllowed()) return undefined;
    let timeline: gsap.core.Timeline | null = null;
    let cancel = (): void => undefined;
    const context = gsap.context(() => {
      const delay = secondsUntil(cues, cue);
      const built = gsap.timeline({
        defaults: { ease: EASE.enter, duration: 0.45 },
        delay,
      });
      builder.current(root, built);
      timeline = built;
      cancel = settleWithin(built, delay + built.duration());
    }, root);
    return () => {
      cancel();
      /* StrictMode's development remount reaches this before the reveal has played. Re-arming lets the
         second mount play it; a reveal that finished stays shown, so a later re-render cannot replay
         it. Progress rather than "were tweens created", which is a different question. */
      if (timeline !== null && timeline.progress() < 1) shown.current = false;
      context.revert();
    };
  }, [ready, cues, cue]);

  return scope;
}

/** A figure that counts up to its value the first time it arrives — and is that value otherwise.
 *
 * **React renders the real figure; this only rewinds it, and only once.** The span's text is the
 * formatted value on every render, so a test, a screen reader arriving late, a reduced-motion reader
 * and a refetch all see the truth. On the first arrival — and only when motion is allowed — the text is
 * set to the starting frame *before paint* and tweened back up to the value it already held.
 *
 * **A refetch never recounts.** A count-up from zero is a statement about arriving, and replaying it
 * when an incident count moves from 7 to 8 would show the reader 0, 1, 2… in a figure that never
 * passed through any of them. Once a count has finished, later values are simply rendered.
 *
 * **The cleanup writes nothing, and that is load-bearing.** React commits a new value's text *before*
 * it runs this effect's cleanup, so a cleanup that "restored the final value" from its own closure
 * wrote the *previous* figure over the one React had just committed — and with the count finished,
 * nothing afterwards would correct it: a count of 7 that became 8 would have gone on reading 7. Killing
 * the tween is enough. On a change mid-count the next run counts again; on unmount the text goes with
 * the element.
 */
export function useCountUp(
  value: number | null,
  format: (n: number) => string,
  cue?: string,
): RefObject<HTMLSpanElement> {
  const ref = useRef<HTMLSpanElement>(null);
  const formatter = useRef(format);
  formatter.current = format;
  const counted = useRef(false);
  const cues = useContext(CueContext);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || value === null || counted.current) return undefined;
    counted.current = true;
    if (!motionAllowed()) return undefined;

    const state = { n: 0 };
    let finished = false;
    element.textContent = formatter.current(0);
    const delay = secondsUntil(cues, cue);
    const tween = gsap.to(state, {
      n: value,
      duration: 0.8,
      ease: EASE.count,
      delay,
      onUpdate: () => {
        element.textContent = formatter.current(state.n);
      },
      onComplete: () => {
        finished = true;
        element.textContent = formatter.current(value);
      },
    });
    const cancel = settleWithin(tween, delay + 0.8);
    return () => {
      cancel();
      tween.kill();
      /* Interrupted rather than finished — StrictMode's remount, or a value that changed mid-count —
         so the next run is still a first arrival and may count. */
      if (!finished) counted.current = false;
    };
  }, [value, cues, cue]);

  return ref;
}

/* ── The screen layer: how every page arrives ──────────────────────────────────────────────────── */

/** The redesign's screen entrance, ported from its `lumiq-motion.js`.
 *
 * **What it does, in the design's own terms.** A screen builds in groups — each top-level block
 * rises 14px through a 6px blur that resolves, on an expo-out curve over 0.7s, 70ms apart. Content
 * that arrives later (a table whose rows loaded, a panel that appeared) enters the same way, lighter
 * and faster, so data arriving reads as arriving rather than as a jump. A bar marked `data-grow`
 * grows from its left edge. Buttons dip to 0.97 on press and settle back.
 *
 * **One hook on `<main>` rather than a call in every screen**, which is the design's choice and the
 * right one for thirty screens: a page gets the motion by being a page, and a screen that
 * choreographs its own arrival (the Overview) opts out with `data-motion="own"` on its root.
 *
 * **Every tween ends on the stylesheet.** Each is a `fromTo` that clears its props when it finishes,
 * and a failsafe forces any tween still running after 1.2s to its end — so a hidden tab, a killed
 * tween or a frame budget blown on a slow machine leaves the page at rest rather than half-faded.
 * Nothing runs without `motionAllowed()`, which is also why jsdom renders every test settled.
 *
 * **No exit fade, which the design has and this does not.** Its 160ms fade-out delays the swap to the
 * next screen; React Router commits the next screen immediately, and holding it back to animate the
 * old one would mean a route change that is not a route change for a sixth of a second. Recorded
 * rather than faked.
 */
export function useScreenMotion<T extends HTMLElement>(routeKey: string): RefObject<T> {
  const scope = useRef<T>(null);

  /* Press feedback, once per mount: every enabled button dips on pointer-down and settles on release. */
  useEffect(() => {
    if (!motionAllowed()) return undefined;
    /* The listeners are on `document`, which inside the Suite shell is the shell's page too: without
       this, every shell button — its sidebar, its user menu — would get Lumiq's press animation. */
    const ours = (button: HTMLButtonElement): boolean =>
      embeddedRuntime()?.owns(button) ?? true;
    const press = (event: PointerEvent): void => {
      const target = event.target as Element | null;
      const button = target?.closest('button');
      if (!(button instanceof HTMLButtonElement) || button.disabled || !ours(button))
        return;
      gsap.to(button, { scale: 0.97, duration: 0.1, ease: 'power2.out' });
    };
    const release = (event: PointerEvent): void => {
      const target = event.target as Element | null;
      const button = target?.closest('button');
      if (!(button instanceof HTMLButtonElement) || !ours(button)) return;
      gsap.to(button, {
        scale: 1,
        duration: 0.35,
        ease: 'power3.out',
        clearProps: 'transform',
      });
    };
    document.addEventListener('pointerdown', press, true);
    document.addEventListener('pointerup', release, true);
    document.addEventListener('pointercancel', release, true);
    return () => {
      document.removeEventListener('pointerdown', press, true);
      document.removeEventListener('pointerup', release, true);
      document.removeEventListener('pointercancel', release, true);
    };
  }, []);

  useLayoutEffect(() => {
    const main = scope.current;
    if (main === null || !motionAllowed()) return undefined;
    const content = main.querySelector('.lq-content') ?? main;
    const seen = new WeakSet<Element>();
    const live = new Set<gsap.core.Tween>();
    let routeAt = performance.now();

    const settle = (tween: gsap.core.Tween): void => {
      live.add(tween);
      window.setTimeout(() => {
        if (tween.progress() < 1) tween.progress(1);
        live.delete(tween);
      }, 1200);
    };
    const visible = (element: Element): boolean =>
      element instanceof HTMLElement &&
      element.tagName !== 'DIALOG' &&
      getComputedStyle(element).position !== 'fixed';
    const owned = (element: Element): boolean =>
      element.closest('[data-motion="own"]') !== null;
    const grow = (root: Element): void => {
      const bars = [
        ...(root.matches('[data-grow]') ? [root] : []),
        ...Array.from(root.querySelectorAll('[data-grow]')),
      ].filter((bar) => !seen.has(bar));
      if (bars.length === 0) return;
      bars.forEach((bar) => seen.add(bar));
      settle(
        gsap.fromTo(
          bars,
          { scaleX: 0, transformOrigin: 'left center' },
          {
            scaleX: 1,
            duration: 1,
            ease: 'expo.out',
            delay: 0.25,
            clearProps: 'transform',
          },
        ),
      );
    };
    const enterScreen = (root: Element): void => {
      if (seen.has(root) || owned(root)) return;
      seen.add(root);
      routeAt = performance.now();
      const blocks = Array.from(root.children).filter(visible);
      blocks.forEach((block) => seen.add(block));
      if (blocks.length > 0) {
        settle(
          gsap.fromTo(
            blocks,
            { autoAlpha: 0, y: 14, filter: 'blur(6px)' },
            {
              autoAlpha: 1,
              y: 0,
              filter: 'blur(0px)',
              ease: 'expo.out',
              duration: 0.7,
              stagger: 0.07,
              clearProps: 'filter,transform,opacity,visibility',
            },
          ),
        );
      }
      grow(root);
    };

    const first = content.firstElementChild;
    if (first !== null) enterScreen(first);

    const observer = new MutationObserver((records) => {
      if (document.hidden) return;
      const blocks: Element[] = [];
      const rows: Element[] = [];
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element) || seen.has(node) || owned(node)) return;
          const parent = node.parentElement;
          if (parent === null) return;
          if (parent === content) {
            enterScreen(node);
            return;
          }
          grow(node);
          if (!visible(node)) return;
          if (parent.tagName === 'TBODY' || parent.getAttribute('role') === 'list') {
            rows.push(node);
          } else if (
            parent === content.firstElementChild ||
            parent.matches('[data-lq-panel]') ||
            node.matches('[data-lq-panel]')
          ) {
            blocks.push(node);
          }
          seen.add(node);
        });
      }
      if (blocks.length > 0) {
        settle(
          gsap.fromTo(
            blocks,
            { autoAlpha: 0, y: 10, filter: 'blur(4px)' },
            {
              autoAlpha: 1,
              y: 0,
              filter: 'blur(0px)',
              ease: 'expo.out',
              duration: 0.55,
              stagger: 0.06,
              clearProps: 'filter,transform,opacity,visibility',
            },
          ),
        );
      }
      if (rows.length > 0) {
        settle(
          gsap.fromTo(
            rows.slice(0, 24),
            { autoAlpha: 0, y: 6 },
            {
              autoAlpha: 1,
              y: 0,
              ease: 'expo.out',
              duration: 0.45,
              stagger: performance.now() - routeAt < 1500 ? 0.03 : 0.015,
              clearProps: 'transform,opacity,visibility',
            },
          ),
        );
      }
    });
    observer.observe(content, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      live.forEach((tween) => {
        tween.progress(1).kill();
      });
      live.clear();
    };
  }, [routeKey]);

  return scope;
}
